/**
 * Election Utilities
 *
 * Shared utility functions for calculating vote shares in both
 * polling and elections. This ensures polls accurately reflect
 * what election outcomes would be at that moment.
 *
 * All functions are pure and deterministic for testability.
 */

import { calculateDistance, normalizeDistance } from '@/lib/ideology'
import type {
  Cohort,
  Party,
  CohortPartyLoyalty,
  PartyDistrictState,
  Candidate,
} from '@/types'

/**
 * @tuning Base weight for ideology alignment in utility calculation
 * Higher values make ideology more important vs other factors
 */
const IDEOLOGY_WEIGHT = 40

/**
 * @tuning Base weight for accumulated loyalty
 * Loyalty represents built-up attachment from past interactions
 */
const LOYALTY_WEIGHT = 30

/**
 * @tuning Base weight for party recognition in district
 * You can't vote for a party you've never heard of
 */
const RECOGNITION_WEIGHT = 20

/**
 * @tuning Base weight for candidate quality
 * Having a charismatic regional candidate helps
 */
const CANDIDATE_WEIGHT = 10

/**
 * @tuning Softmax temperature for vote probability calculation
 * Lower = more deterministic (votes go to highest utility)
 * Higher = more random (closer to uniform distribution)
 */
const SOFTMAX_TEMPERATURE = 20

/**
 * @tuning Minimum recognition needed to get any votes
 * Parties with 0 recognition can still get tiny vote share
 */
const MIN_RECOGNITION_FACTOR = 0.05

/**
 * Calculate the utility a cohort assigns to a party.
 * Higher utility = more likely to vote for this party.
 *
 * Utility is based on:
 * 1. Ideology alignment (weighted by cohort's salience)
 * 2. Accumulated loyalty from past interactions
 * 3. Party recognition in the district
 * 4. Candidate quality (if regional candidate assigned)
 *
 * @param cohort - The cohort evaluating the party
 * @param party - The party being evaluated
 * @param loyalty - Loyalty record (if any) between cohort and party
 * @param districtState - Party's presence in the cohort's district
 * @param candidate - Regional candidate assigned to district (if any)
 * @returns Utility score (higher = more attractive)
 */
export function calculateCohortUtility(
  cohort: Cohort,
  party: Party,
  loyalty: CohortPartyLoyalty | undefined,
  districtState: PartyDistrictState | undefined,
  candidate: Candidate | null | undefined
): number {
  // 1. Ideology alignment (0-100 where 100 = perfect match)
  const distance = calculateDistance(
    party.ideology_vector,
    cohort.ideology_vector,
    cohort.salience_weights
  )
  const normalizedDistance = normalizeDistance(distance)
  const ideologyScore = 100 - normalizedDistance

  // 2. Loyalty score (0-100, defaults to 0 for unknown parties)
  const loyaltyScore = loyalty?.loyalty_score ?? 0

  // 3. Recognition factor (0-100, with minimum floor)
  const recognition = districtState?.recognition ?? 0
  const recognitionScore = Math.max(MIN_RECOGNITION_FACTOR * 100, recognition)

  // 4. Candidate quality bonus (0-100 based on charisma)
  // Only applies if a regional candidate is assigned to this district
  const candidateScore = candidate ? candidate.charisma : 0

  // Weighted combination
  const utility =
    (ideologyScore * IDEOLOGY_WEIGHT +
      loyaltyScore * LOYALTY_WEIGHT +
      recognitionScore * RECOGNITION_WEIGHT +
      candidateScore * CANDIDATE_WEIGHT) /
    (IDEOLOGY_WEIGHT + LOYALTY_WEIGHT + RECOGNITION_WEIGHT + CANDIDATE_WEIGHT)

  return utility
}

/**
 * Result of vote share calculation for all parties
 */
export interface VoteShareResult {
  /** Map from party ID to vote share percentage (0-100) */
  voteShares: Map<string, number>
  /** Total number of voters across all cohorts */
  totalVoters: number
  /** Breakdown by party: raw vote counts */
  voteCounts: Map<string, number>
}

/**
 * Calculate true vote shares for all parties based on cohort utilities.
 * Uses softmax to convert utilities into vote probabilities.
 *
 * This is the core calculation that both polling and elections use.
 * Polls add noise to these results; elections use them directly.
 *
 * @param cohorts - All cohorts in the area being calculated
 * @param parties - All parties competing for votes
 * @param loyalties - All loyalty records for these cohorts
 * @param districtStates - Party recognition/organization data
 * @param candidates - All candidates (for regional candidate bonus)
 * @returns Vote share percentages for each party
 */
export function calculateVoteShares(
  cohorts: Cohort[],
  parties: Party[],
  loyalties: CohortPartyLoyalty[],
  districtStates: PartyDistrictState[],
  candidates: Candidate[]
): VoteShareResult {
  const partyVotes = new Map<string, number>()
  parties.forEach((p) => partyVotes.set(p.id, 0))

  let totalVoters = 0

  for (const cohort of cohorts) {
    // Calculate utility for each party
    const utilities = parties.map((party) => {
      const loyalty = loyalties.find(
        (l) => l.cohort_id === cohort.id && l.party_id === party.id
      )
      const districtState = districtStates.find(
        (ds) => ds.party_id === party.id && ds.district_id === cohort.district_id
      )
      const regionalCandidate = candidates.find(
        (c) =>
          c.party_id === party.id &&
          c.assigned_district_id === cohort.district_id &&
          c.role === 'regional_candidate'
      )

      return {
        partyId: party.id,
        utility: calculateCohortUtility(
          cohort,
          party,
          loyalty,
          districtState,
          regionalCandidate
        ),
      }
    })

    // Convert utilities to vote probabilities using softmax
    // This ensures parties with higher utility get more votes,
    // but doesn't give 100% to the highest utility party
    const expUtilities = utilities.map((u) =>
      Math.exp(u.utility / SOFTMAX_TEMPERATURE)
    )
    const totalExp = expUtilities.reduce((sum, exp) => sum + exp, 0)

    // Calculate turnout for this cohort
    const turnoutRate = cohort.base_turnout * (1 + cohort.current_turnout_modifier)
    const turnoutVoters = cohort.population * turnoutRate

    // Distribute voters to parties based on softmax probabilities
    for (let i = 0; i < utilities.length; i++) {
      const probability = expUtilities[i] / totalExp
      const votes = turnoutVoters * probability
      const currentVotes = partyVotes.get(utilities[i].partyId) || 0
      partyVotes.set(utilities[i].partyId, currentVotes + votes)
    }

    totalVoters += turnoutVoters
  }

  // Convert to percentages
  const voteShares = new Map<string, number>()
  for (const [partyId, votes] of partyVotes) {
    voteShares.set(partyId, totalVoters > 0 ? (votes / totalVoters) * 100 : 0)
  }

  return {
    voteShares,
    totalVoters,
    voteCounts: partyVotes,
  }
}

/**
 * Calculate per-cohort utility breakdown for detailed analysis.
 * Used for generating cohort breakdowns in detailed polls.
 *
 * @param cohort - The cohort to analyze
 * @param parties - All parties to compare
 * @param loyalties - Loyalty records
 * @param districtStates - Recognition data
 * @param candidates - Candidate data
 * @returns Array of party utilities for this cohort, sorted by utility
 */
export function calculateCohortUtilities(
  cohort: Cohort,
  parties: Party[],
  loyalties: CohortPartyLoyalty[],
  districtStates: PartyDistrictState[],
  candidates: Candidate[]
): Array<{ partyId: string; partyName: string; utility: number }> {
  return parties
    .map((party) => {
      const loyalty = loyalties.find(
        (l) => l.cohort_id === cohort.id && l.party_id === party.id
      )
      const districtState = districtStates.find(
        (ds) => ds.party_id === party.id && ds.district_id === cohort.district_id
      )
      const regionalCandidate = candidates.find(
        (c) =>
          c.party_id === party.id &&
          c.assigned_district_id === cohort.district_id &&
          c.role === 'regional_candidate'
      )

      return {
        partyId: party.id,
        partyName: party.name,
        utility: calculateCohortUtility(
          cohort,
          party,
          loyalty,
          districtState,
          regionalCandidate
        ),
      }
    })
    .sort((a, b) => b.utility - a.utility)
}
